import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Divider,
  Avatar,
  Progress,
  Modal,
  Form,
  Input,
  Upload,
  Descriptions,
  Empty,
  Spin,
  Alert,
  Tooltip,
  Pagination,
} from "antd";
import {
  FaTasks,
  FaArrowLeft,
  FaCheckCircle,
  FaPauseCircle,
  FaPlayCircle,
  FaClock,
  FaCalendarAlt,
  FaUsers,
  FaPlus,
  FaFile,
  FaExclamationCircle,
  FaHistory,
  FaUpload,
  FaHourglass,
  FaChartLine,
  FaFlagCheckered,
  FaBolt,
  FaSnowflake,
  FaUserShield,
  FaCog,
  FaTimesCircle,
  FaStar,
  FaClipboardCheck,
  FaLightbulb,
  FaTools,
  FaCommentAlt,
  FaBan,
  FaUser,
  FaPaperclip,
  FaRegClock,
  FaAward,
  FaThumbsUp,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import Api from "../api/Api";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

/* ─────────────────────────────────────────────────────────────────────────────
   DESIGN TOKENS
───────────────────────────────────────────────────────────────────────────── */
const tokens = {
  blue: "#1677ff",
  blueLight: "#e6f4ff",
  green: "#52c41a",
  greenLight: "#f6ffed",
  red: "#ff4d4f",
  redLight: "#fff1f0",
  orange: "#fa8c16",
  orangeLight: "#fff7e6",
  purple: "#722ed1",
  purpleLight: "#f9f0ff",
  teal: "#13c2c2",
  tealLight: "#e6fffb",
  gold: "#faad14",
  goldLight: "#fffbe6",
  gray: "#8c8c8c",
  grayLight: "#fafafa",
  border: "#f0f0f0",
  shadow: "0 2px 12px rgba(0,0,0,0.06)",
  shadowMd: "0 4px 20px rgba(0,0,0,0.08)",
  radius: 14,
  radiusSm: 8,
};

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDateTime = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return str;
  }
};

const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return str;
  }
};

const formatMinutosParaHMS = (minutos) => {
  if (!minutos || minutos === 0) return "0h 0m";
  const absMinutos = Math.abs(minutos);
  const horas = Math.floor(absMinutos / 60);
  const mins = absMinutos % 60;
  const sinal = minutos < 0 ? "-" : "";
  return `${sinal}${horas}h ${mins}m`;
};

const getStatusInfo = (id_status) => {
  const map = {
    1: { label: "ABERTO", color: "blue", hex: tokens.blue, bg: tokens.blueLight, icon: <FaPlayCircle /> },
    2: { label: "FECHADO", color: "green", hex: tokens.green, bg: tokens.greenLight, icon: <FaCheckCircle /> },
    3: { label: "SUSPENSO", color: "orange", hex: tokens.orange, bg: tokens.orangeLight, icon: <FaPauseCircle /> },
    4: { label: "EM ANÁLISE", color: "purple", hex: tokens.purple, bg: tokens.purpleLight, icon: <FaClock /> },
  };
  return map[id_status] || { label: "DESCONHECIDO", color: "default", hex: "#d9d9d9", bg: "#fafafa", icon: null };
};

const ratingColor = (nota) => {
  const map = { 1: tokens.red, 2: tokens.orange, 3: tokens.gold, 4: tokens.green, 5: tokens.blue };
  return map[nota] || tokens.gray;
};

/* ─────────────────────────────────────────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────────────────────────────────────────── */
const SectionHeader = ({ icon, title, color = tokens.blue, extra }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 20,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          background: color + "18",
          color,
          borderRadius: 8,
          padding: "7px 9px",
          display: "flex",
          alignItems: "center",
          fontSize: 15,
        }}
      >
        {icon}
      </div>
      <Text strong style={{ fontSize: 15, color: "#1f1f1f", letterSpacing: "-0.01em" }}>
        {title}
      </Text>
    </div>
    {extra}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   STAR ROW
───────────────────────────────────────────────────────────────────────────── */
const StarRow = ({ nota, label }) => {
  const color = ratingColor(nota);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ display: "flex", gap: 3 }}>
        {[1, 2, 3, 4, 5].map((s) => (
          <span key={s} style={{ color: s <= nota ? color : "#e8e8e8", fontSize: 15 }}>
            ★
          </span>
        ))}
      </div>
      <Text
        style={{
          fontSize: 12,
          fontWeight: 600,
          color,
          background: color + "18",
          padding: "1px 8px",
          borderRadius: 20,
        }}
      >
        {label}
      </Text>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   AVALIAÇÃO DO DIRETOR
───────────────────────────────────────────────────────────────────────────── */
const AvaliacaoDiretor = ({ tarefa, classificacoes }) => {
  const {
    qualidade_entrega,
    espirito_equipa,
    iniciativa_proatividade,
    resolucao_problema,
    recomencao,
  } = tarefa || {};

  if (!qualidade_entrega && !espirito_equipa && !iniciativa_proatividade && !resolucao_problema)
    return null;

  const getLabel = (nota) =>
    classificacoes.find((c) => c.nota === nota)?.avaliacao || nota;

  const criterios = [
    { key: "qualidade_entrega", label: "Qualidade de Entrega", nota: qualidade_entrega, icon: <FaClipboardCheck /> },
    { key: "espirito_equipa", label: "Espírito de Equipa", nota: espirito_equipa, icon: <FaUsers /> },
    { key: "iniciativa_proatividade", label: "Iniciativa & Proatividade", nota: iniciativa_proatividade, icon: <FaLightbulb /> },
    { key: "resolucao_problema", label: "Resolução de Problemas", nota: resolucao_problema, icon: <FaTools /> },
  ];

  const notas = criterios.map((c) => c.nota).filter(Boolean);
  const media = notas.length ? (notas.reduce((a, b) => a + b, 0) / notas.length).toFixed(1) : null;
  const mediaColor = media
    ? media >= 4.5 ? tokens.blue : media >= 3.5 ? tokens.green : media >= 2.5 ? tokens.gold : tokens.red
    : tokens.gray;

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: tokens.radius,
        border: `1.5px solid ${tokens.green}30`,
        overflow: "hidden",
        boxShadow: tokens.shadow,
        height: "100%",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: `linear-gradient(135deg, ${tokens.green}15 0%, ${tokens.teal}10 100%)`,
          padding: "18px 24px",
          borderBottom: `1px solid ${tokens.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              background: tokens.green + "20",
              color: tokens.green,
              borderRadius: 8,
              padding: "7px 9px",
              display: "flex",
              fontSize: 15,
            }}
          >
            <FaAward />
          </div>
          <div>
            <Text strong style={{ fontSize: 15, display: "block" }}>
              Avaliação do Diretor
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Avaliação de desempenho registada na aprovação
            </Text>
          </div>
        </div>
        {media && (
          <div
            style={{
              background: mediaColor + "18",
              border: `1.5px solid ${mediaColor}40`,
              borderRadius: 12,
              padding: "8px 16px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 800, color: mediaColor, lineHeight: 1 }}>
              {media}
            </div>
            <div style={{ fontSize: 10, color: tokens.gray, marginTop: 2, fontWeight: 500 }}>
              MÉDIA
            </div>
          </div>
        )}
      </div>

      {/* Critérios */}
      <div style={{ padding: "20px 24px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {criterios.map((c) => (
            <div
              key={c.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                background: tokens.grayLight,
                borderRadius: tokens.radiusSm,
                border: `1px solid ${tokens.border}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: ratingColor(c.nota), fontSize: 13 }}>{c.icon}</span>
                <Text style={{ fontSize: 13, fontWeight: 500 }}>{c.label}</Text>
              </div>
              <StarRow nota={c.nota} label={getLabel(c.nota)} />
            </div>
          ))}
        </div>

        {/* Recomendação */}
        {recomencao && (
          <div
            style={{
              marginTop: 16,
              padding: "14px 16px",
              background: tokens.tealLight,
              border: `1px solid ${tokens.teal}30`,
              borderRadius: tokens.radiusSm,
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
            }}
          >
            <FaCommentAlt style={{ color: tokens.teal, marginTop: 2, flexShrink: 0 }} />
            <div>
              <Text style={{ fontSize: 12, color: tokens.teal, fontWeight: 600, display: "block", marginBottom: 4 }}>
                RECOMENDAÇÃO
              </Text>
              <Text style={{ fontSize: 13, color: "#1f1f1f" }}>{recomencao}</Text>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MÉTRICAS DE TEMPO
───────────────────────────────────────────────────────────────────────────── */
const MetricasTempo = ({ relatorio }) => {
  if (!relatorio?.atraso) return null;
  const { atraso, minutos_suspensos, data_fecho } = relatorio;

  const styleMap = {
    atrasado: { bg: tokens.redLight, border: tokens.red + "40", text: tokens.red, icon: <FaExclamationCircle /> },
    antecipado: { bg: tokens.greenLight, border: tokens.green + "40", text: tokens.green, icon: <FaBolt /> },
    no_prazo: { bg: tokens.tealLight, border: tokens.teal + "40", text: tokens.teal, icon: <FaFlagCheckered /> },
  };
  const s = styleMap[atraso.status] || styleMap.no_prazo;

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: tokens.radius,
        border: `1.5px solid ${s.border}`,
        overflow: "hidden",
        boxShadow: tokens.shadow,
        height: "100%",
      }}
    >
      <div
        style={{
          padding: "16px 22px",
          background: s.bg,
          borderBottom: `1px solid ${s.border}`,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ color: s.text, fontSize: 16 }}>{s.icon}</span>
        <div>
          <Text strong style={{ color: s.text, fontSize: 14 }}>{atraso.formatado}</Text>
          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Análise de tempo da tarefa</Text>
        </div>
      </div>

      <div style={{ padding: "16px 22px" }}>
        <Row gutter={[12, 12]}>
          <Col xs={12}>
            <div style={{ background: tokens.grayLight, borderRadius: tokens.radiusSm, padding: "12px 14px", border: `1px solid ${tokens.border}` }}>
              <Text type="secondary" style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</Text>
              <Text strong style={{ fontSize: 13, color: s.text, display: "block", marginTop: 2 }}>
                {atraso.status === "atrasado" ? "Atrasada" : atraso.status === "antecipado" ? "Antecipada" : "No Prazo"}
              </Text>
            </div>
          </Col>

          {(atraso.minutos_atraso > 0 || atraso.minutos_antecipacao > 0) && (
            <Col xs={12}>
              <div style={{ background: s.bg, borderRadius: tokens.radiusSm, padding: "12px 14px", border: `1px solid ${s.border}` }}>
                <Text type="secondary" style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {atraso.status === "antecipado" ? "Antecipação" : "Atraso"}
                </Text>
                <Text strong style={{ fontSize: 13, color: s.text, display: "block", marginTop: 2 }}>
                  {atraso.status === "antecipado" ? formatMinutosParaHMS(atraso.minutos_antecipacao) : formatMinutosParaHMS(atraso.minutos_atraso)}
                </Text>
              </div>
            </Col>
          )}

          {minutos_suspensos > 0 && (
            <Col xs={12}>
              <div style={{ background: tokens.goldLight, borderRadius: tokens.radiusSm, padding: "12px 14px", border: `1px solid ${tokens.gold}40` }}>
                <Text type="secondary" style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Tempo Suspenso</Text>
                <Text strong style={{ fontSize: 13, color: tokens.gold, display: "block", marginTop: 2 }}>{formatMinutosParaHMS(minutos_suspensos)}</Text>
              </div>
            </Col>
          )}

          <Col xs={12}>
            <div style={{ background: tokens.grayLight, borderRadius: tokens.radiusSm, padding: "12px 14px", border: `1px solid ${tokens.border}` }}>
              <Text type="secondary" style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Conclusão</Text>
              <Text strong style={{ fontSize: 13, color: data_fecho ? tokens.green : tokens.gold, display: "block", marginTop: 2 }}>
                {data_fecho ? "Concluída" : "Em Andamento"}
              </Text>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   HISTÓRICO DE REJEIÇÕES com paginação
───────────────────────────────────────────────────────────────────────────── */
const PAGE_SIZE_REJEICOES = 5;

const HistoricoRejeicoes = ({ rejeicoes }) => {
  const [currentPage, setCurrentPage] = useState(1);

  if (!rejeicoes || rejeicoes.length === 0) return null;

  const paginated = rejeicoes.slice(
    (currentPage - 1) * PAGE_SIZE_REJEICOES,
    currentPage * PAGE_SIZE_REJEICOES
  );

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: tokens.radius,
        border: `1.5px solid ${tokens.red}25`,
        overflow: "hidden",
        boxShadow: tokens.shadow,
        marginBottom: 24,
      }}
    >
      <div
        style={{
          background: `linear-gradient(135deg, ${tokens.red}10 0%, ${tokens.orange}08 100%)`,
          padding: "18px 24px",
          borderBottom: `1px solid ${tokens.border}`,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            background: tokens.red + "18",
            color: tokens.red,
            borderRadius: 8,
            padding: "7px 9px",
            display: "flex",
            fontSize: 15,
          }}
        >
          <FaBan />
        </div>
        <div>
          <Text strong style={{ fontSize: 15, display: "block" }}>
            Histórico de Rejeições
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {rejeicoes.length} rejeição{rejeicoes.length > 1 ? "ões" : ""} registada{rejeicoes.length > 1 ? "s" : ""}
          </Text>
        </div>
      </div>

      <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
        <Row gutter={[16, 16]}>
          {paginated.map((r, idx) => {
            const globalIdx = (currentPage - 1) * PAGE_SIZE_REJEICOES + idx;
            return (
              <Col xs={24} md={12} key={r.id}>
                <div
                  style={{
                    display: "flex",
                    gap: 14,
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: tokens.red + "18",
                      border: `2px solid ${tokens.red}40`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: tokens.red,
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {rejeicoes.length - globalIdx}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      background: tokens.grayLight,
                      borderRadius: tokens.radiusSm,
                      border: `1px solid ${tokens.border}`,
                      padding: "12px 16px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <Text type="secondary" style={{ fontSize: 11, fontWeight: 500 }}>
                        {formatDate(r.data)}
                      </Text>
                      <Tag
                        style={{
                          fontSize: 10,
                          background: tokens.red + "12",
                          color: tokens.red,
                          border: `1px solid ${tokens.red}25`,
                          borderRadius: 20,
                          padding: "0 8px",
                        }}
                      >
                        REJEITADA
                      </Tag>
                    </div>
                    {r.motivo ? (
                      <Text style={{ fontSize: 13, color: "#1f1f1f", display: "block", lineHeight: 1.6 }}>
                        {r.motivo}
                      </Text>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
                        Sem motivo registado
                      </Text>
                    )}
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>

        {rejeicoes.length > PAGE_SIZE_REJEICOES && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 8, paddingTop: 16, borderTop: `1px solid ${tokens.border}` }}>
            <Pagination
              current={currentPage}
              pageSize={PAGE_SIZE_REJEICOES}
              total={rejeicoes.length}
              onChange={setCurrentPage}
              size="small"
              showTotal={(total) => `${total} rejeições`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   ATUALIZAÇÕES com paginação
───────────────────────────────────────────────────────────────────────────── */
const PAGE_SIZE_ATUALIZACOES = 5;

const ListaAtualizacoes = ({ atualizacoes, loading }) => {
  const [currentPage, setCurrentPage] = useState(1);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!atualizacoes || atualizacoes.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0", color: tokens.gray }}>
        <FaHistory style={{ fontSize: 36, opacity: 0.25, marginBottom: 12 }} />
        <div style={{ fontSize: 14, color: tokens.gray }}>Nenhuma atualização ainda</div>
        <div style={{ fontSize: 12, color: "#bfbfbf", marginTop: 4 }}>
          As atualizações da tarefa aparecerão aqui
        </div>
      </div>
    );
  }

  const paginated = atualizacoes.slice(
    (currentPage - 1) * PAGE_SIZE_ATUALIZACOES,
    currentPage * PAGE_SIZE_ATUALIZACOES
  );

  return (
    <div>
      {/* Grid de atualizações em duas colunas */}
      <Row gutter={[20, 20]}>
        {paginated.map((item) => (
          <Col xs={24} md={12} key={item.id}>
            <div
              style={{
                background: tokens.grayLight,
                border: `1px solid ${tokens.border}`,
                borderRadius: tokens.radiusSm,
                padding: "16px 18px",
                height: "100%",
              }}
            >
              {/* Meta row */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <Avatar
                  size={32}
                  style={{
                    background: `linear-gradient(135deg, ${tokens.blue} 0%, #4096ff 100%)`,
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                  icon={<FaUser style={{ fontSize: 12 }} />}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: 12, fontWeight: 600, color: "#1f1f1f", display: "block" }}>
                    {item.nome || "Colaborador"}
                  </Text>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <FaRegClock style={{ fontSize: 10 }} />
                    {formatDateTime(item.created_at)}
                  </Text>
                </div>
              </div>

              {/* Conteúdo */}
              <div
                style={{
                  background: "#fff",
                  border: `1px solid ${tokens.border}`,
                  borderRadius: tokens.radiusSm,
                  padding: "12px 14px",
                }}
              >
                {item.descricao && (
                  <Paragraph
                    style={{
                      margin: 0,
                      fontSize: 13,
                      lineHeight: 1.75,
                      color: "#333",
                    }}
                  >
                    {item.descricao}
                  </Paragraph>
                )}

                {item.file && (
                  <a
                    href={item.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      marginTop: item.descricao ? 10 : 0,
                      padding: "6px 12px",
                      background: tokens.blueLight,
                      border: `1px solid ${tokens.blue}30`,
                      borderRadius: 20,
                      color: tokens.blue,
                      fontSize: 12,
                      fontWeight: 500,
                      textDecoration: "none",
                    }}
                  >
                    <FaPaperclip style={{ fontSize: 11 }} />
                    Ver Anexo
                  </a>
                )}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {atualizacoes.length > PAGE_SIZE_ATUALIZACOES && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${tokens.border}` }}>
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE_ATUALIZACOES}
            total={atualizacoes.length}
            onChange={setCurrentPage}
            size="small"
            showTotal={(total) => `${total} atualizações`}
          />
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   SUSPENSÕES com paginação
───────────────────────────────────────────────────────────────────────────── */
const PAGE_SIZE_SUSPENSOES = 4;

const ListaSuspensoes = ({ suspensoes }) => {
  const [currentPage, setCurrentPage] = useState(1);

  if (!suspensoes || suspensoes.length === 0) return null;

  const paginated = suspensoes.slice(
    (currentPage - 1) * PAGE_SIZE_SUSPENSOES,
    currentPage * PAGE_SIZE_SUSPENSOES
  );

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: tokens.radius,
        border: `1px solid ${tokens.border}`,
        boxShadow: tokens.shadow,
        padding: "22px 26px",
        marginBottom: 24,
      }}
    >
      <SectionHeader icon={<FaPauseCircle />} title="Períodos de Suspensão" color={tokens.orange} />
      <Row gutter={[14, 14]}>
        {paginated.map((s, idx) => (
          <Col xs={24} sm={12} key={idx}>
            <div
              style={{
                background: s.em_curso ? tokens.orangeLight : tokens.grayLight,
                border: `1px solid ${s.em_curso ? tokens.orange + "40" : tokens.border}`,
                borderRadius: tokens.radiusSm,
                padding: "14px 16px",
                height: "100%",
              }}
            >
              {s.em_curso && (
                <Tag color="orange" style={{ borderRadius: 20, marginBottom: 8, fontSize: 10 }}>
                  Em Curso
                </Tag>
              )}
              <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                Motivo
              </Text>
              <Text strong style={{ fontSize: 12, display: "block", marginBottom: 10 }}>
                {s.descricao || "—"}
              </Text>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {formatDateTime(s.entrada)} → {s.saida ? formatDateTime(s.saida) : "Em curso"}
                </Text>
                <Text strong style={{ fontSize: 12, color: tokens.orange }}>
                  {formatMinutosParaHMS(s.minutos)}
                </Text>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {suspensoes.length > PAGE_SIZE_SUSPENSOES && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 16, paddingTop: 14, borderTop: `1px solid ${tokens.border}` }}>
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE_SUSPENSOES}
            total={suspensoes.length}
            onChange={setCurrentPage}
            size="small"
            showTotal={(total) => `${total} suspensões`}
          />
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL NOVA ATUALIZAÇÃO
───────────────────────────────────────────────────────────────────────────── */
const AtualizacaoFormModal = ({ open, onClose, onSuccess, tarefaId, userId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    if (!open) { form.resetFields(); setFileList([]); }
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const formData = new FormData();
      formData.append("descricao", values.descricao || "");
      formData.append("id_user", userId);
      formData.append("id_tarefa", tarefaId);
      if (fileList.length > 0) formData.append("file", fileList[0]);
      await Api.addAtualizacaoTarefa(formData);
      toast.success("Atualização adicionada com sucesso!");
      form.resetFields(); setFileList([]);
      onSuccess(); onClose();
    } catch (error) {
      if (error.errorFields) toast.error("Preencha os campos obrigatórios.");
      else toast.error("Erro ao adicionar atualização.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      centered
      destroyOnClose
      styles={{ content: { borderRadius: 16, padding: 0, overflow: "hidden" }, body: { padding: 0 } }}
    >
      <div style={{ background: `linear-gradient(135deg, ${tokens.blue} 0%, #4096ff 100%)`, padding: "22px 28px", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: 8, padding: "8px 10px", display: "flex", color: "#fff" }}>
          <FaPlus style={{ fontSize: 16 }} />
        </div>
        <div>
          <Title level={5} style={{ color: "#fff", margin: 0 }}>Nova Atualização</Title>
          <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>Registar progresso da tarefa</Text>
        </div>
      </div>

      <div style={{ padding: "24px 28px" }}>
        <Form form={form} layout="vertical">
          <Form.Item label={<Text strong style={{ fontSize: 13 }}>Descrição</Text>} name="descricao">
            <TextArea rows={4} placeholder="Descreva o progresso ou atualização..." style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item label={<Text strong style={{ fontSize: 13 }}>Anexo <Text type="secondary" style={{ fontWeight: 400 }}>(opcional)</Text></Text>}>
            <Upload
              onRemove={() => setFileList([])}
              beforeUpload={(file) => { setFileList([file]); return false; }}
              fileList={fileList}
              maxCount={1}
            >
              <Button icon={<FaUpload style={{ marginRight: 6 }} />} style={{ borderRadius: 8 }}>
                Selecionar Arquivo
              </Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: "block" }}>Máximo 10MB</Text>
          </Form.Item>
        </Form>
      </div>

      <div style={{ padding: "16px 28px", borderTop: `1px solid ${tokens.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <Button onClick={onClose} style={{ borderRadius: 8 }}>Cancelar</Button>
        <Button type="primary" onClick={handleSubmit} loading={loading} style={{ borderRadius: 8, fontWeight: 600 }}>
          Adicionar
        </Button>
      </div>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL ALTERAR STATUS
───────────────────────────────────────────────────────────────────────────── */
const AlterarStatusModal = ({ open, onClose, onSuccess, tarefaId, statusAtual, isDiretor }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [novoStatus, setNovoStatus] = useState(null);

  useEffect(() => {
    if (!open) { form.resetFields(); setNovoStatus(null); }
  }, [open, form]);

  const getOpcoes = () => {
    if (statusAtual === 1) {
      const opcoes = [{ id: 4, label: "EM ANÁLISE", hex: tokens.purple, icon: <FaClock />, requerMotivo: false }];
      if (isDiretor) opcoes.unshift({ id: 3, label: "SUSPENSO", hex: tokens.orange, icon: <FaPauseCircle />, requerMotivo: true, apenasDir: true });
      return opcoes;
    }
    if (statusAtual === 3) return [{ id: 1, label: "ABERTO", hex: tokens.blue, icon: <FaPlayCircle />, requerMotivo: false }];
    return [];
  };

  const opcoes = getOpcoes();
  const opcaoSelecionada = opcoes.find((o) => o.id === novoStatus);
  const requerMotivo = opcaoSelecionada?.requerMotivo ?? false;

  const handleSubmit = async () => {
    if (!novoStatus) { toast.warning("Selecione um status!"); return; }
    let descricao = "";
    if (requerMotivo) {
      try { const v = await form.validateFields(); descricao = v.descricao; } catch { return; }
    }
    try {
      setLoading(true);
      await Api.AlterStatusTarefa({ id: tarefaId, id_status: novoStatus, ...(requerMotivo && { descricao }) });
      toast.success("Status alterado com sucesso!");
      onSuccess(novoStatus); onClose();
    } catch { toast.error("Erro ao alterar status."); }
    finally { setLoading(false); }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      centered
      destroyOnClose
      styles={{ content: { borderRadius: 16, padding: 0, overflow: "hidden" }, body: { padding: 0 } }}
    >
      <div style={{ background: `linear-gradient(135deg, ${tokens.blue} 0%, #4096ff 100%)`, padding: "22px 28px", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: 8, padding: "8px 10px", display: "flex", color: "#fff" }}>
          <FaExclamationCircle style={{ fontSize: 16 }} />
        </div>
        <Title level={5} style={{ color: "#fff", margin: 0 }}>Alterar Status</Title>
      </div>

      <div style={{ padding: "24px 28px" }}>
        {opcoes.length === 0 ? (
          <Empty description="Nenhuma alteração disponível" />
        ) : (
          <>
            <Text style={{ fontSize: 13, color: tokens.gray, display: "block", marginBottom: 14 }}>Selecione o novo status:</Text>
            <Space direction="vertical" style={{ width: "100%" }} size={10}>
              {opcoes.map((op) => (
                <div
                  key={op.id}
                  onClick={() => setNovoStatus(op.id)}
                  style={{
                    border: `2px solid ${novoStatus === op.id ? op.hex : tokens.border}`,
                    background: novoStatus === op.id ? op.hex + "0e" : "#fff",
                    borderRadius: tokens.radiusSm,
                    padding: "14px 18px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    transition: "all 0.18s",
                  }}
                >
                  <span style={{ color: op.hex, fontSize: 18 }}>{op.icon}</span>
                  <Text strong style={{ fontSize: 14, color: op.hex }}>{op.label}</Text>
                  {op.apenasDir && <Tag color="purple" style={{ marginLeft: "auto", borderRadius: 20, fontSize: 11 }}>Apenas Diretor</Tag>}
                </div>
              ))}
            </Space>

            {requerMotivo && (
              <>
                <Divider style={{ margin: "18px 0" }} />
                <Alert
                  message="Motivo obrigatório para suspensão"
                  type="info"
                  showIcon
                  style={{ borderRadius: 8, marginBottom: 14 }}
                />
                <Form form={form} layout="vertical">
                  <Form.Item
                    label={<Text strong style={{ fontSize: 13 }}>Motivo da Suspensão</Text>}
                    name="descricao"
                    rules={[{ required: true, message: "Informe o motivo da suspensão." }]}
                  >
                    <TextArea rows={3} placeholder="Descreva o motivo..." style={{ borderRadius: 8 }} />
                  </Form.Item>
                </Form>
              </>
            )}
          </>
        )}
      </div>

      <div style={{ padding: "16px 28px", borderTop: `1px solid ${tokens.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <Button onClick={onClose} style={{ borderRadius: 8 }}>Cancelar</Button>
        <Button type="primary" onClick={handleSubmit} loading={loading} disabled={!novoStatus} style={{ borderRadius: 8, fontWeight: 600 }}>
          Confirmar
        </Button>
      </div>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   TAREFA DETALHE — PÁGINA PRINCIPAL
───────────────────────────────────────────────────────────────────────────── */
const TarefaDetalhe = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tarefa: tarefaInicial } = location.state || {};

  const [relatorio, setRelatorio] = useState(null);
  const [atualizacoes, setAtualizacoes] = useState([]);
  const [rejeicoes, setRejeicoes] = useState([]);
  const [classificacoes, setClassificacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [atualizacaoModalOpen, setAtualizacaoModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  const userStored = JSON.parse(localStorage.getItem("user")) || {};
  const isDiretor = userStored?.diretor === "S";

  useEffect(() => {
    document.title = `${tarefaInicial?.titulo || "Tarefa"} | Dashboard`;
  }, [tarefaInicial]);

  const loadDados = useCallback(async () => {
    if (!tarefaInicial?.id) return;
    try {
      setLoading(true);
      const [respRelatorio, respAtualizacoes, respRejeicoes, respClassificacoes] = await Promise.all([
        Api.ListRelatorioTarefa({ id: tarefaInicial.id }),
        Api.ListAtualizacaoTarefa({ id: tarefaInicial.id }),
        Api.ListRejeicaoTarefa({ id: tarefaInicial.id }).catch(() => []),
        Api.ListClassificacao().catch(() => []),
      ]);
      setRelatorio(respRelatorio?.data || null);
      setAtualizacoes(Array.isArray(respAtualizacoes) ? respAtualizacoes : []);
      setRejeicoes(Array.isArray(respRejeicoes) ? respRejeicoes : []);
      setClassificacoes(Array.isArray(respClassificacoes) ? respClassificacoes : []);
    } catch {
      toast.error("Erro ao carregar dados da tarefa.");
    } finally {
      setLoading(false);
    }
  }, [tarefaInicial]);

  useEffect(() => {
    if (!tarefaInicial) { navigate("/tarefas"); return; }
    loadDados();
  }, [tarefaInicial, navigate, loadDados]);

  const isResponsavel = () => relatorio?.responsaveis?.some((r) => r.id === userStored.id);

  // ✅ Apenas permite adicionar atualizações quando id_status === 1 (Aberta)
  const canAddUpdate = () => isResponsavel() && tarefaInicial?.id_status === 1;

  const canChangeStatus = () => isResponsavel() && (tarefaInicial?.id_status === 1 || tarefaInicial?.id_status === 3);

  const handleStatusSuccess = (novoIdStatus) => {
    if (tarefaInicial) tarefaInicial.id_status = novoIdStatus;
    loadDados();
  };

  const temAvaliacao = !!(
    tarefaInicial?.qualidade_entrega ||
    tarefaInicial?.espirito_equipa ||
    tarefaInicial?.iniciativa_proatividade ||
    tarefaInicial?.resolucao_problema
  );

  const temAnalise = !!relatorio?.atraso;

  if (!tarefaInicial) return null;

  const statusInfo = getStatusInfo(tarefaInicial?.id_status);

  if (loading && !relatorio) {
    return (
      <Layout style={{ background: "transparent", minHeight: "100vh" }}>
        <Content style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <Spin size="large" />
        </Content>
      </Layout>
    );
  }

  // Spans dinâmicos para a segunda linha (Responsáveis + Avaliação + Análise)
  const respColSpan = (temAvaliacao && temAnalise) ? 8 : (temAvaliacao || temAnalise) ? 12 : 24;
  const avalColSpan = temAnalise ? 8 : 12;
  const tempoColSpan = temAvaliacao ? 8 : 12;

  return (
    <Layout style={{ background: "transparent" }}>
      <Content style={{ padding: "28px 24px", maxWidth: 1600, margin: "0 auto", width: "100%" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* ── TOPO ── */}
        <div style={{ marginBottom: 28 }}>
 <Button
  icon={<FaArrowLeft style={{ marginRight: 6 }} />}
  onClick={() => navigate(-1)}
  style={{
    borderRadius: 8,
    marginBottom: 20,
    border: `1px solid ${tokens.border}`,
    fontWeight: 500
  }}
>
  Voltar
</Button>

          {tarefaInicial?.id_status !== 1 && tarefaInicial?.id_status !== 3 && (
            <Alert
              message={
                tarefaInicial?.id_status === 2
                  ? "Tarefa Fechada — Esta tarefa foi concluída e não aceita mais atualizações."
                  : tarefaInicial?.id_status === 4
                  ? "Tarefa em Análise — Não é possível adicionar atualizações enquanto a tarefa está em análise."
                  : "Não é possível adicionar atualizações com o status atual."
              }
              type={tarefaInicial?.id_status === 2 ? "success" : "info"}
              showIcon
              icon={tarefaInicial?.id_status === 2 ? <FaCheckCircle /> : <FaClock />}
              style={{ marginBottom: 20, borderRadius: 10 }}
            />
          )}

          {tarefaInicial?.id_status === 3 && (
            <Alert
              message="Tarefa Suspensa — Não é possível adicionar atualizações enquanto esta tarefa estiver suspensa."
              type="warning"
              showIcon
              icon={<FaPauseCircle />}
              style={{ marginBottom: 20, borderRadius: 10 }}
            />
          )}

          {/* Hero card */}
          <div
            style={{
              background: `linear-gradient(135deg, ${tokens.blue} 0%, #3b8dff 50%, #5ba3ff 100%)`,
              borderRadius: tokens.radius + 2,
              padding: "28px 36px",
              boxShadow: "0 8px 32px rgba(22,119,255,0.2)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "absolute", top: -40, right: -40, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", bottom: -20, right: 80, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />

            <Row align="middle" gutter={[24, 16]}>
              <Col flex="auto">
                <Space align="start" size={16}>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.18)",
                      borderRadius: 12,
                      padding: 14,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <FaTasks style={{ fontSize: 28, color: "#fff" }} />
                  </div>
                  <div>
                    <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {tarefaInicial.etapa || "Tarefa"} · #{tarefaInicial.id}
                    </Text>
                    <Title level={2} style={{ color: "#fff", margin: "4px 0 8px", fontWeight: 700, lineHeight: 1.25 }}>
                      {relatorio?.titulo || tarefaInicial.titulo}
                    </Title>
                    <Space wrap size={8}>
                      <Tag
                        style={{
                          borderRadius: 20,
                          padding: "4px 14px",
                          fontSize: 12,
                          fontWeight: 700,
                          background: "rgba(255,255,255,0.2)",
                          border: "1px solid rgba(255,255,255,0.35)",
                          color: "#fff",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {statusInfo.icon} {statusInfo.label}
                      </Tag>
                      {relatorio?.percentagem !== undefined && (
                        <Tag style={{ borderRadius: 20, padding: "4px 14px", fontSize: 12, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff" }}>
                          {parseFloat(relatorio.percentagem) || 0}% concluído
                        </Tag>
                      )}
                    </Space>
                  </div>
                </Space>
              </Col>
              <Col>
                <Space>
                  {canChangeStatus() && (
                    <Button
                      onClick={() => setStatusModalOpen(true)}
                      style={{ borderRadius: 8, background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.35)", color: "#fff", fontWeight: 600 }}
                    >
                      Alterar Status
                    </Button>
                  )}
                  {canAddUpdate() && (
                    <Button
                      type="primary"
                      icon={<FaPlus style={{ marginRight: 6 }} />}
                      onClick={() => setAtualizacaoModalOpen(true)}
                      style={{ borderRadius: 8, background: "#fff", color: tokens.blue, border: "none", fontWeight: 700, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                    >
                      Nova Atualização
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </div>
        </div>

        {/* ── LINHA 1: Descrição + Informações + Progresso/Status ── */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          {/* Descrição */}
          <Col xs={24} md={10}>
            <div
              style={{
                background: "#fff",
                borderRadius: tokens.radius,
                border: `1px solid ${tokens.border}`,
                boxShadow: tokens.shadow,
                padding: "22px 26px",
                height: "100%",
              }}
            >
              <SectionHeader icon={<FaTasks />} title="Descrição" />
              <Paragraph style={{ fontSize: 14, lineHeight: 1.85, color: "#444", margin: 0 }}>
                {relatorio?.descricao || "Sem descrição disponível."}
              </Paragraph>
            </div>
          </Col>

          {/* Informações (trocou de lugar com Responsáveis) */}
          <Col xs={24} md={7}>
            <div
              style={{
                background: "#fff",
                borderRadius: tokens.radius,
                border: `1px solid ${tokens.border}`,
                boxShadow: tokens.shadow,
                padding: "22px 26px",
                height: "100%",
              }}
            >
              <SectionHeader icon={<FaCalendarAlt />} title="Informações" />
              <Row gutter={[14, 14]}>
                {[
                  { label: "Início", value: formatDateTime(relatorio?.inicio), color: tokens.blue },
                  { label: "Fim Previsto", value: formatDateTime(relatorio?.fim_previsto), color: tokens.orange },
                  { label: "Data de Fecho", value: relatorio?.data_fecho ? formatDateTime(relatorio.data_fecho) : "—", color: tokens.green },
                  {
                    label: "Progresso",
                    value: null,
                    custom: (
                      <Progress
                        percent={parseFloat(relatorio?.percentagem) || 0}
                        size="small"
                        strokeColor={{ "0%": tokens.blue, "100%": tokens.green }}
                        status={parseFloat(relatorio?.percentagem) === 100 ? "success" : "active"}
                      />
                    ),
                  },
                ].map((item) => (
                  <Col xs={24} sm={12} key={item.label}>
                    <div
                      style={{
                        background: tokens.grayLight,
                        borderRadius: tokens.radiusSm,
                        padding: "14px 16px",
                        border: `1px solid ${tokens.border}`,
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 5 }}>
                        {item.label}
                      </Text>
                      {item.custom || (
                        <Text strong style={{ fontSize: 13, color: item.color }}>
                          {item.value}
                        </Text>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </Col>

          {/* Progresso + Status */}
          <Col xs={24} md={7}>
            <div
              style={{
                background: "#fff",
                borderRadius: tokens.radius,
                border: `1px solid ${tokens.border}`,
                boxShadow: tokens.shadow,
                padding: "22px 26px",
                height: "100%",
                textAlign: "center",
              }}
            >
              <Progress
                type="circle"
                percent={parseFloat(relatorio?.percentagem) || 0}
                size={110}
                strokeColor={{ "0%": tokens.blue, "100%": tokens.green }}
                strokeWidth={8}
                format={(p) => (
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#1f1f1f" }}>{p}%</div>
                    <div style={{ fontSize: 10, color: tokens.gray, marginTop: 2 }}>PROGRESSO</div>
                  </div>
                )}
              />
              <div style={{ marginTop: 14 }}>
                <Tag
                  style={{
                    borderRadius: 20,
                    padding: "5px 16px",
                    fontSize: 13,
                    fontWeight: 700,
                    background: statusInfo.bg || tokens.blueLight,
                    color: statusInfo.hex || tokens.blue,
                    border: `1.5px solid ${(statusInfo.hex || tokens.blue) + "30"}`,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {statusInfo.icon} {statusInfo.label}
                </Tag>
              </div>
            </div>
          </Col>
        </Row>

        {/* ── LINHA 2: Responsáveis + Avaliação do Diretor + Análise de Tempo ── */}
        {(temAvaliacao || temAnalise) ? (
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            {/* Responsáveis (trocou de lugar com Informações) */}
            <Col xs={24} md={respColSpan}>
              <div
                style={{
                  background: "#fff",
                  borderRadius: tokens.radius,
                  border: `1px solid ${tokens.border}`,
                  boxShadow: tokens.shadow,
                  padding: "22px 26px",
                  height: "100%",
                }}
              >
                <SectionHeader icon={<FaUsers />} title="Responsáveis" />
                {loading ? (
                  <div style={{ textAlign: "center", padding: "20px 0" }}><Spin /></div>
                ) : !relatorio?.responsaveis?.length ? (
                  <Empty description="Sem responsáveis" />
                ) : (
                  <Space direction="vertical" style={{ width: "100%" }} size={10}>
                    {relatorio.responsaveis.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px 14px",
                          background: r.id === userStored.id ? tokens.blueLight : tokens.grayLight,
                          borderRadius: tokens.radiusSm,
                          border: `1px solid ${r.id === userStored.id ? tokens.blue + "30" : tokens.border}`,
                        }}
                      >
                        <Avatar
                          size={38}
                          style={{
                            background: `linear-gradient(135deg, ${tokens.blue} 0%, #4096ff 100%)`,
                            fontSize: 14,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {r.nome.charAt(0).toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ fontSize: 13, display: "block" }}>{r.nome}</Text>
                          {r.id === userStored.id && (
                            <Tag style={{ borderRadius: 20, fontSize: 10, background: tokens.blue + "15", color: tokens.blue, border: "none", marginTop: 2 }}>Você</Tag>
                          )}
                          {r.escala && (
                            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
                              {r.escala.turno} · {r.escala.entrada}–{r.escala.saida}
                            </Text>
                          )}
                        </div>
                      </div>
                    ))}
                  </Space>
                )}
              </div>
            </Col>

            {/* Avaliação do Diretor */}
            {temAvaliacao && (
              <Col xs={24} md={avalColSpan}>
                <AvaliacaoDiretor tarefa={tarefaInicial} classificacoes={classificacoes} />
              </Col>
            )}

            {/* Análise de Tempo */}
            {temAnalise && (
              <Col xs={24} md={tempoColSpan}>
                <MetricasTempo relatorio={relatorio} />
              </Col>
            )}
          </Row>
        ) : (
          /* Se não houver nem Avaliação nem Análise de Tempo, os Responsáveis ocupam a linha toda */
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24}>
              <div
                style={{
                  background: "#fff",
                  borderRadius: tokens.radius,
                  border: `1px solid ${tokens.border}`,
                  boxShadow: tokens.shadow,
                  padding: "22px 26px",
                }}
              >
                <SectionHeader icon={<FaUsers />} title="Responsáveis" />
                {loading ? (
                  <div style={{ textAlign: "center", padding: "20px 0" }}><Spin /></div>
                ) : !relatorio?.responsaveis?.length ? (
                  <Empty description="Sem responsáveis" />
                ) : (
                  <Space direction="vertical" style={{ width: "100%" }} size={10}>
                    {relatorio.responsaveis.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px 14px",
                          background: r.id === userStored.id ? tokens.blueLight : tokens.grayLight,
                          borderRadius: tokens.radiusSm,
                          border: `1px solid ${r.id === userStored.id ? tokens.blue + "30" : tokens.border}`,
                        }}
                      >
                        <Avatar
                          size={38}
                          style={{
                            background: `linear-gradient(135deg, ${tokens.blue} 0%, #4096ff 100%)`,
                            fontSize: 14,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {r.nome.charAt(0).toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ fontSize: 13, display: "block" }}>{r.nome}</Text>
                          {r.id === userStored.id && (
                            <Tag style={{ borderRadius: 20, fontSize: 10, background: tokens.blue + "15", color: tokens.blue, border: "none", marginTop: 2 }}>Você</Tag>
                          )}
                          {r.escala && (
                            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
                              {r.escala.turno} · {r.escala.entrada}–{r.escala.saida}
                            </Text>
                          )}
                        </div>
                      </div>
                    ))}
                  </Space>
                )}
              </div>
            </Col>
          </Row>
        )}

        {/* ── SUSPENSÕES (largura total) ── */}
        <ListaSuspensoes suspensoes={relatorio?.suspensoes} />

        {/* ── HISTÓRICO DE REJEIÇÕES (largura total) ── */}
        <HistoricoRejeicoes rejeicoes={rejeicoes} />

        {/* ── ATUALIZAÇÕES (largura total) ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: tokens.radius,
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadow,
            padding: "22px 26px",
            marginBottom: 24,
          }}
        >
          <SectionHeader
            icon={<FaHistory />}
            title="Atualizações"
            extra={
              atualizacoes.length > 0 && (
                <Tag style={{ borderRadius: 20, background: tokens.blueLight, color: tokens.blue, border: "none", fontWeight: 600 }}>
                  {atualizacoes.length}
                </Tag>
              )
            }
          />
          <ListaAtualizacoes atualizacoes={atualizacoes} loading={loading} />
        </div>

        {/* ── MODAIS ── */}
        <AtualizacaoFormModal
          open={atualizacaoModalOpen}
          onClose={() => setAtualizacaoModalOpen(false)}
          onSuccess={loadDados}
          tarefaId={tarefaInicial.id}
          userId={userStored.id}
        />
        <AlterarStatusModal
          open={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          onSuccess={handleStatusSuccess}
          tarefaId={tarefaInicial.id}
          statusAtual={tarefaInicial.id_status}
          isDiretor={isDiretor}
        />
      </Content>
    </Layout>
  );
};

export default TarefaDetalhe;